import { useMutation } from "@tanstack/react-query";
import { saveAs } from "file-saver";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { fetchDataGetBlobFile } from "@/core/api/api-methods";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";

interface Args {
  batchId: string;
}

/**
 * Télécharge le relevé global PDF d'une paie lancée (`GET
 * /PayrollBatches/{id}/statement`) : synthèse (statut, salariés payés, masse,
 * frais, total débité, total reçu, solde après la paie), pièce comptable
 * synthétique et détail de chaque ligne (téléphones masqués). Codes : 200
 * `application/pdf` ; 404 ; 422 aucun salarié payé.
 */
export const useDownloadPayrollBatchStatement = () =>
  useMutation({
    mutationKey: ["payroll", "payrollBatches", "statement"],
    mutationFn: async ({ batchId }: Args) => {
      const { blob, filename } = await fetchDataGetBlobFile({
        url: payrollEndpoints.payrollBatches.statement(batchId),
        acceptHeader: "application/pdf",
      });
      if (blob.type.includes("json")) {
        const text = await blob.text();
        throw new Error(text || "Payroll statement download failed");
      }
      saveAs(blob, filename ?? `releve-paie-${batchId}.pdf`);
      return filename;
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useDownloadPayrollBatchStatement failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
